import {
  Box,
  Card,
  Divider,
  Flex,
  Stack,
  Switch,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { ReactElement, useState } from "react";
import { ISelectStorageProps } from "./types";
import ResponsiveLineReturn from "@common/components/text/ResponsiveLineReturn";
import StorageSlider from "@common/components/ui/form/StorageSlider";
import { ConfirmStorageRemovalModal } from "./SelectStorage.components";
import { useAddons } from "@stores/addons/hooks";
import { ADDON_PRODUCT_DWH_PREPAID_UNITS_DEFAULT } from "@stores/addons/constants";
import { useStyles } from "./SelectStorage.styled";
import IconStorage from "@common/components/icons/IconStorage";
import { formatPrice } from "@common/utils/string";
import { useDisclosure } from "@mantine/hooks";

export default function SelectStorage({
  "data-testid": dataTestid = "SelectStorage",
  disabled,
  isYearly = false,
  hasDatabase = false,
  defaultValue = ADDON_PRODUCT_DWH_PREPAID_UNITS_DEFAULT,
  value = ADDON_PRODUCT_DWH_PREPAID_UNITS_DEFAULT,
  handleChange,
  ...cardProps
}: ISelectStorageProps): ReactElement {
  const theme = useMantineTheme();
  const { classes } = useStyles();

  const [storageFirstChange, setStorageFirstChange] = useState(true);

  const [hasStorage, setHasStorage] = useState(hasDatabase);
  const [storageAmount, setStorageAmount] = useState(value);

  // Storage removal confirmation modal
  const [
    openedConfirmStorageRemovalModal,
    {
      open: openConfirmStorageRemovalModal,
      close: closeConfirmStorageRemovalModal,
    },
  ] = useDisclosure(false);

  const handleHasStorageChange = (checked: boolean) => {
    if (storageFirstChange && hasStorage) {
      openConfirmStorageRemovalModal();
    } else {
      setStorageFirstChange(false);
      setHasStorage(checked);
      handleChange(checked ? storageAmount : undefined);
    }
  };

  const handleStorageChange = (value: number) => {
    setStorageAmount(value);
    handleChange(hasStorage ? value : undefined);
  };

  const handleCloseModal = (removeStorage) => {
    closeConfirmStorageRemovalModal();

    if (removeStorage !== undefined) {
      setStorageFirstChange(false);

      if (removeStorage) {
        setHasStorage(false);
        handleChange(undefined);
      }
    }
  };

  // add-ons price
  const [storageAddOn] = useAddons({
    yearly: isYearly,
    dwh: true,
  });

  // billing frequency
  const billingFrequencyText = isYearly ? "year" : "month";

  return (
    <>
      <Card
        data-testid={dataTestid}
        className={classes.card}
        bg="gray.2"
        p={0}
        w="100%"
        {...cardProps}
      >
        <Flex
          data-testid={`${dataTestid}_switch-container`}
          align="center"
          direction="row"
          gap="xs"
          justify="space-between"
          pl="md"
          pr="xl"
          py="md"
          w="100%"
        >
          <Box miw={48}>
            <IconStorage />
          </Box>

          <Box w="100%">
            <Text fw={700} fz="sm">
              Don&apos;t have a database?
            </Text>
            <Text fz="xs" mt={theme.spacing.xs / 2}>
              Add secure storage to let your team upload CSVs and spreadsheets
              <ResponsiveLineReturn invert />
              to your Metabase directly.
            </Text>
          </Box>

          <Switch
            data-testid={`${dataTestid}_switch`}
            defaultChecked={hasStorage}
            checked={hasStorage}
            onChange={(event) =>
              handleHasStorageChange(event.currentTarget.checked)
            }
            disabled={disabled}
          />
        </Flex>

        {hasStorage && storageAmount && (
          <>
            <Divider color="gray.3" w="100%" />

            <Stack spacing="sm" pb="xl" pt="md" px="xl" w="100%">
              <Flex
                align="flex-start"
                direction="row"
                gap="sm"
                justify="space-between"
                w="100%"
              >
                <Box>
                  <Text fz="sm">Select storage size (rows)</Text>
                  <Text fz="xs">
                    Start with a smaller storage package. You will be able to
                    change this later.
                  </Text>
                </Box>

                <Text fw={700} fz="sm">
                  {formatPrice(
                    storageAmount * storageAddOn.default_price_per_unit,
                  )}
                  /{billingFrequencyText}
                </Text>
              </Flex>

              <StorageSlider
                data-testid={`${dataTestid}_slider`}
                disabled={disabled}
                price={storageAddOn.default_price_per_unit}
                isYearly={isYearly}
                defaultValue={defaultValue}
                value={storageAmount}
                onChange={handleStorageChange}
                onChangeEnd={handleStorageChange}
              />
            </Stack>
          </>
        )}
      </Card>

      <ConfirmStorageRemovalModal
        opened={openedConfirmStorageRemovalModal}
        onClose={handleCloseModal}
      />
    </>
  );
}
